import React, { useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useTerminology from '@sift/skipper/helpers/useTerminology';
import { useStatus, isLoading, isNormal, isError } from 'resift';
import usePrevious from '@sift/resift/usePrevious';
import makePublishEntityType from 'fetches/makePublishEntityType';
import { useDispatch } from 'react-redux';
import showSnackbar from 'helpers/showSnackbar';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import CancelButton from 'components/Page/CancelButton';
import LoadingButton from 'atoms/LoadingButton';

interface Props {
  entityTypeKey: string;
  isActive: boolean;
  name: string;
  open: boolean;
  onSwitchIsActive: () => void;
  onSetOpen: (state: boolean) => void;
}

function PublishEntityTypeModal({
  entityTypeKey,
  isActive,
  open,
  name,
  onSwitchIsActive,
  onSetOpen,
}: Props) {
  const classes = useStyles();
  const terminology = useTerminology();
  const dispatch = useDispatch();
  const action = isActive ? 'publish' : 'unpublish';
  const publishEntityType = makePublishEntityType(entityTypeKey);
  const status = useStatus(publishEntityType);
  const prevStatus = usePrevious(status);
  const subtitle = isActive
    ? `After pages are published, they will become available to all ${terminology.plural}.`
    : `After pages are unpublished, they will only be available to assigned editors.`;

  const handleSave = () => {
    dispatch(publishEntityType(isActive));
  };

  const handleCancel = () => {
    onSetOpen(false);
    onSwitchIsActive();
  };

  useEffect(() => {
    if (isLoading(prevStatus) && isNormal(status)) {
      onSetOpen(false);
    } else if (isLoading(prevStatus) && isError(status)) {
      onSetOpen(false);
    }
  }, [status, prevStatus]);

  showSnackbar({
    status: status,
    message: isActive
      ? 'Pages have been published and are available to all users.'
      : 'Pages have been unpublished and are visible to assigned editors.',
    errorMessage: `Failed to ${action} pages. Please try again.`,
    variant: 'success',
  });

  return (
    <Dialog data-testid="publish-entityType-modal" open={open} onClose={handleCancel}>
      <DialogContent className={classes.marginBottom}>
        <Typography variant="h5">
          Are you sure you want to {action} {name} Pages?
        </Typography>
        <Typography>{subtitle}</Typography>
      </DialogContent>
      <DialogActions>
        <CancelButton disabled={isLoading(status)} onClick={handleCancel} />
        <LoadingButton
          color={action === 'publish' ? 'secondary' : undefined}
          label={action}
          variant={action === 'publish' ? 'contained' : 'text'}
          onClick={handleSave}
          loading={isLoading(status)}
        />
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: any) => ({
  marginBottom: theme.spacing(3),
}));

export default PublishEntityTypeModal;
